/**
 * Report Header & Footer settings page (FEAT-2026-0005, Phase 2 — reduced).
 *
 * The per-test template editor was retired: all reports are now code-based.
 * The only admin-editable template that remains is the cross-report
 * "Shared Header & Footer" chrome, so this page auto-selects it and hides
 * the old per-test picker rail.
 *
 *   • Centre  — Form / JSON tabs over the working draft
 *   • Right   — live PDF preview
 *
 * Top toolbar: Undo · Redo · History · Compare · Reset · Save.
 * Cmd+Z / Cmd+Shift+Z handled via global key listener (zundo store).
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Stack, Typography, Paper, Tabs, Tab, Tooltip, Chip, Snackbar, Alert,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Undo, Redo, History as HistoryIcon, RestoreOutlined,
  Save as SaveIcon, CompareArrowsOutlined, SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import {
  useReportTemplateStore, useReportTemplateTimeline,
} from './useReportTemplateStore';
import {
  reportTemplateService,
  StaleVersionError,
  type ReportTemplateSummary,
} from '../../../services/reportTemplateService';
import { FormPane } from './FormPane';
import { JsonPane } from './JsonPane';
import { PreviewPane } from './PreviewPane';
import { VersionHistoryDrawer } from './VersionHistoryDrawer';
import { CompareDialog } from './CompareDialog';

type PaneTab = 'form' | 'json';

/**
 * TestName of the sole admin-editable template — the cross-report shared
 * header/footer chrome that renders on ALL PDF reports. The template list
 * is filtered down to this single entry.
 */
const SHARED_HEADER_FOOTER_TEST_NAME = 'Shared Header & Footer';

export const ReportTemplatesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const templateKey = useReportTemplateStore((s) => s.templateKey);
  const setTemplateKey = useReportTemplateStore((s) => s.setTemplateKey);
  const draftConfig = useReportTemplateStore((s) => s.draftConfig);
  const publishedConfig = useReportTemplateStore((s) => s.publishedConfig);
  const resetDraftConfig = useReportTemplateStore((s) => s.resetDraftConfig);
  const bumpPdfRefreshRequest = useReportTemplateStore((s) => s.bumpPdfRefreshRequest);
  const setDraftConfig = useReportTemplateStore((s) => s.setDraftConfig);

  const [templates, setTemplates] = useState<ReportTemplateSummary[]>([]);
  const [activeSummary, setActiveSummary] = useState<ReportTemplateSummary | null>(null);
  const [paneTab, setPaneTab] = useState<PaneTab>('form');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compareDialog, setCompareDialog] = useState<{ a: number; b: number; draft?: boolean } | null>(null);
  const [saveDialog, setSaveDialog] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' | 'info' } | null>(null);
  /**
   * Phase 6: stale-version snackbar with a "Reload latest" action. Distinct
   * from `snack` because (a) it stays visible until the user dismisses or
   * reloads (no auto-hide — the user needs to make a decision), and (b) it
   * carries an action button so we keep its state separate from the
   * generic success / error / info snack queue.
   */
  const [staleSnack, setStaleSnack] = useState<{
    currentVersionNumber: number | null;
    currentVersionPublishedBy: string | null;
    currentVersionPublishedAt: string | null;
  } | null>(null);

  // ── Load template list on mount ────────────────────────────────────────
  // The per-test editor is retired, so we filter the backend's template
  // list down to the sole admin-editable entry — the cross-report Shared
  // Header & Footer chrome — and auto-select it. There's no picker rail
  // anymore, so there's nothing to deep-link to; the single HF template is
  // always what loads.
  useEffect(() => {
    reportTemplateService.list()
      .then((rows) => {
        const hf = rows.filter((r) => r.TestName === SHARED_HEADER_FOOTER_TEST_NAME);
        setTemplates(hf);
        if (hf.length > 0) {
          setTemplateKey(hf[0].ReportTemplateKey);
        }
      })
      .catch((err) => setSnack({ msg: `Failed to load templates: ${err?.message ?? err}`, severity: 'error' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the URL whenever the user picks a different template — uses
  // replace so the browser-back stack doesn't fill up with every click.
  useEffect(() => {
    if (!templateKey) return;
    if (searchParams.get('template') === templateKey) return;
    const next = new URLSearchParams(searchParams);
    next.set('template', templateKey);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  // ── Whenever the selected template changes, hydrate the draft ──────────
  useEffect(() => {
    if (!templateKey) return;
    const summary = templates.find((t) => t.ReportTemplateKey === templateKey) ?? null;
    setActiveSummary(summary);
    // Clear draft + publishedConfig SYNCHRONOUSLY so the preview pane
    // doesn't fire a doomed POST with the previous template's config
    // shape while the fetch is in flight (root cause of the PSD 400
    // the user kept seeing right after switching templates from
    // Atterberg).
    resetDraftConfig(null);
    reportTemplateService.get(templateKey)
      .then((out) => {
        if (out.currentVersion) {
          resetDraftConfig(out.currentVersion.parsedConfig);
        }
      })
      .catch((err) => setSnack({ msg: `Failed to load template: ${err?.message ?? err}`, severity: 'error' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, templates]);

  // ── Undo / redo via keyboard ───────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const cmd = e.metaKey || e.ctrlKey;
      if (cmd && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        useReportTemplateTimeline().undo();
      } else if (cmd && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        useReportTemplateTimeline().redo();
      } else if (cmd && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        setSaveDialog(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const dirty = useMemo(() =>
    publishedConfig != null && JSON.stringify(draftConfig) !== JSON.stringify(publishedConfig),
  [draftConfig, publishedConfig]);

  // ── Toolbar actions ────────────────────────────────────────────────────
  const onUndo = () => useReportTemplateTimeline().undo();
  const onRedo = () => useReportTemplateTimeline().redo();
  const canUndo = useReportTemplateTimeline().pastStates.length > 0;
  const canRedo = useReportTemplateTimeline().futureStates.length > 0;

  const onResetToCurrent = () => {
    if (publishedConfig == null) return;
    if (!dirty) return;
    if (!window.confirm('Discard unsaved edits and revert to the active version?')) return;
    setDraftConfig(publishedConfig);
  };

  /**
   * Stage the schema-registered factory defaults as a draft edit. Doesn't
   * publish — the user reviews via the live preview, then hits Save to make
   * it a new version. Tracked through the zundo history so Undo / Redo work.
   */
  const onResetToFactory = async () => {
    if (!templateKey) return;
    if (!window.confirm(
      'Replace the current draft with the schema factory defaults? '
      + 'You can review in the preview, then click Save to publish.'
    )) return;
    try {
      const factory = await reportTemplateService.getFactory(templateKey);
      setDraftConfig(factory);
      setSnack({ msg: 'Factory defaults loaded. Click Save to publish as a new version.', severity: 'info' });
    } catch (err: any) {
      setSnack({ msg: `Couldn’t load factory: ${err?.message ?? err}`, severity: 'error' });
    }
  };

  const onSaveSubmit = async () => {
    if (!templateKey) return;
    setBusy(true);
    try {
      // Phase 6: pass the version the draft was based on so the backend
      // can detect concurrent saves. NULL means "first publish" (template
      // has never had a CurrentVersionKey) — the backend allows the
      // initial v1 in that case but rejects everything else.
      const expectedBaseVersion = activeSummary?.CurrentVersionNumber ?? null;
      const v = await reportTemplateService.saveVersion(templateKey, {
        config: draftConfig,
        changeNote: changeNote.trim() || null,
        expectedBaseVersion,
      });
      resetDraftConfig(v.parsedConfig);
      setSaveDialog(false);
      setChangeNote('');
      setSnack({ msg: `Published v${v.version.VersionNumber}`, severity: 'success' });
      // Refresh template list so the toolbar shows the updated version count.
      const rows = await reportTemplateService.list();
      setTemplates(rows);
      // Phase 8.3: tell the right-pane PDF preview to re-render against the
      // just-published config. The pane no longer auto-refreshes on edits;
      // a successful save is one of the explicit triggers.
      bumpPdfRefreshRequest();
    } catch (err: unknown) {
      // Phase 6 — concurrent-save conflict. The user's expectedBaseVersion
      // was stale; we don't have enough context to auto-merge (and per the
      // plan, NEVER auto-merge — admins should review the change), so we
      // show a sticky snackbar with a "Reload latest" action that fetches
      // the new server-side version and replaces the draft.
      if (err instanceof StaleVersionError) {
        setSaveDialog(false);
        setStaleSnack({
          currentVersionNumber: err.payload.currentVersionNumber,
          currentVersionPublishedBy: err.payload.currentVersionPublishedBy,
          currentVersionPublishedAt: err.payload.currentVersionPublishedAt,
        });
        // Refresh the template list so the toolbar's version chip is
        // up-to-date even before the user picks Reload-latest. This makes
        // the chip's number match the snackbar copy.
        try {
          const rows = await reportTemplateService.list();
          setTemplates(rows);
        } catch { /* non-fatal */ }
        return;
      }
      const detail =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as { message?: string })?.message
        ?? String(err);
      setSnack({ msg: `Save failed: ${detail}`, severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Phase 6 stale-snackbar action — fetch the freshly-published version
   * and replace the draft with it. Confirms first if the draft is dirty
   * since the user's local edits would be discarded.
   */
  const onReloadLatest = async () => {
    if (!templateKey) return;
    if (dirty && !window.confirm(
      'Reloading the latest published version will replace your unsaved edits. Continue?'
    )) return;
    try {
      const out = await reportTemplateService.get(templateKey);
      if (out.currentVersion) resetDraftConfig(out.currentVersion.parsedConfig);
      const rows = await reportTemplateService.list();
      setTemplates(rows);
      setStaleSnack(null);
      setSnack({ msg: `Loaded the latest published version.`, severity: 'info' });
    } catch (err: unknown) {
      const detail = (err as { message?: string })?.message ?? String(err);
      setSnack({ msg: `Reload failed: ${detail}`, severity: 'error' });
    }
  };

  const onAfterRestore = async () => {
    if (!templateKey) return;
    // Reload the template + active version.
    const out = await reportTemplateService.get(templateKey);
    if (out.currentVersion) resetDraftConfig(out.currentVersion.parsedConfig);
    const rows = await reportTemplateService.list();
    setTemplates(rows);
    setSnack({ msg: 'Restored. New version is now active.', severity: 'info' });
  };

  /** Compare an OLDER saved version against the current (called from history). */
  const handleCompare = (versionNumber: number) => {
    if (!activeSummary?.CurrentVersionNumber) return;
    setCompareDialog({ a: versionNumber, b: activeSummary.CurrentVersionNumber });
  };
  /** Compare the in-memory DRAFT against the current saved version (toolbar). */
  const handleCompareDraft = () => {
    if (!activeSummary?.CurrentVersionNumber) return;
    setCompareDialog({
      a: activeSummary.CurrentVersionNumber,
      b: activeSummary.CurrentVersionNumber,    // dialog ignores `b` in draft mode
      draft: true,
    });
  };

  const handleViewVersion = async (versionNumber: number) => {
    if (!templateKey) return;
    const v = await reportTemplateService.getVersion(templateKey, versionNumber);
    setDraftConfig(v.parsedConfig);
    setHistoryOpen(false);
    setSnack({ msg: `Loaded v${versionNumber} into the editor (not yet saved).`, severity: 'info' });
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 140px)' }}>
      {/* The per-test picker rail was retired — the Shared Header & Footer
          template is the only editable entry and is auto-selected on load,
          so the editor spans the full width. */}

      {/* Centre + right panes */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Toolbar */}
        <Paper variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, mr: 1 }}>
            {activeSummary?.TestName ?? '—'}
          </Typography>
          {activeSummary?.CurrentVersionNumber && (
            <Chip size="small" label={`v${activeSummary.CurrentVersionNumber}`} />
          )}
          {dirty && <Chip size="small" label="unsaved edits" color="warning" />}

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Undo (Cmd+Z)">
            <span>
              <Button size="small" startIcon={<Undo fontSize="small" />} disabled={!canUndo} onClick={onUndo}>Undo</Button>
            </span>
          </Tooltip>
          <Tooltip title="Redo (Cmd+Shift+Z)">
            <span>
              <Button size="small" startIcon={<Redo fontSize="small" />} disabled={!canRedo} onClick={onRedo}>Redo</Button>
            </span>
          </Tooltip>
          <Tooltip title="Discard unsaved edits and revert to the active version">
            <span>
              <Button size="small" startIcon={<RestoreOutlined fontSize="small" />} disabled={!dirty} onClick={onResetToCurrent}>Reset</Button>
            </span>
          </Tooltip>
          <Tooltip title="Load the schema-registered factory defaults as a draft (review, then Save to publish)">
            <span>
              <Button size="small" startIcon={<SettingsBackupRestoreOutlined fontSize="small" />}
                disabled={!templateKey} onClick={onResetToFactory}>
                Factory
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Open version history">
            <Button size="small" startIcon={<HistoryIcon fontSize="small" />} onClick={() => setHistoryOpen(true)}>History</Button>
          </Tooltip>
          <Tooltip title="Compare draft against the active version">
            <span>
              <Button size="small" startIcon={<CompareArrowsOutlined fontSize="small" />}
                disabled={!dirty || !activeSummary?.CurrentVersionNumber}
                onClick={handleCompareDraft}>
                Compare
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Save as new version (Cmd+S)">
            <span>
              <Button size="small" variant="contained" startIcon={<SaveIcon fontSize="small" />}
                disabled={!dirty || busy} onClick={() => setSaveDialog(true)}>
                Save
              </Button>
            </span>
          </Tooltip>
        </Paper>

        {/* Centre + right split */}
        <Box sx={{ flex: 1, display: 'flex', gap: 1, minHeight: 0 }}>
          <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Tabs value={paneTab} onChange={(_e, v) => setPaneTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
              <Tab label="Form" value="form" sx={{ minHeight: 36, fontSize: '0.8rem' }} />
              <Tab label="JSON" value="json" sx={{ minHeight: 36, fontSize: '0.8rem' }} />
            </Tabs>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!templateKey ? (
                <Typography sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  Loading the shared header &amp; footer template…
                </Typography>
              ) : paneTab === 'form' ? (
                <FormPane templateKey={templateKey} testName={activeSummary?.TestName ?? null} />
              ) : (
                <JsonPane templateKey={templateKey} />
              )}
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ width: '50%', display: 'flex', flexDirection: 'column', minWidth: 320 }}>
            {templateKey && <PreviewPane templateKey={templateKey} testName={activeSummary?.TestName ?? null} />}
          </Paper>
        </Box>
      </Box>

      {/* Drawer + dialogs */}
      {templateKey && (
        <VersionHistoryDrawer
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          templateKey={templateKey}
          currentVersionNumber={activeSummary?.CurrentVersionNumber ?? null}
          onAfterRestore={onAfterRestore}
          onViewVersion={handleViewVersion}
          onCompareVersion={handleCompare}
        />
      )}
      {templateKey && compareDialog && (
        <CompareDialog
          open={!!compareDialog}
          onClose={() => setCompareDialog(null)}
          templateKey={templateKey}
          versionA={compareDialog.a}
          versionB={compareDialog.b}
          draftConfig={compareDialog.draft ? draftConfig : null}
        />
      )}
      <Dialog open={saveDialog} onClose={() => setSaveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save as new version</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A new version will be created and immediately activated. The previous version stays in
            history and can be restored at any time.
          </Typography>
          <TextField
            label="Change note (optional)"
            placeholder="e.g. larger plasticity chart, tighter axis fonts"
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            fullWidth multiline maxRows={3} autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={onSaveSubmit}>Publish</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>

      {/*
        Phase 6 — stale-version snackbar. Sticks until the user dismisses
        or reloads (no autoHide) because the user has to make a decision:
          • Reload latest → replaces draft with the new server version,
            losing any in-flight local edits.
          • Keep editing  → dismisses; the next save will 409 again.
        Anchored top-center so it's not in the same corner as the regular
        snackbar — distinct visual treatment for a distinct UX situation.
      */}
      <Snackbar
        open={!!staleSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {staleSnack ? (
          <Alert
            severity="warning"
            onClose={() => setStaleSnack(null)}
            sx={{ minWidth: 480 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setStaleSnack(null)}
                >
                  Keep editing
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={onReloadLatest}
                >
                  Reload latest
                </Button>
              </Stack>
            }
          >
            {(() => {
              const v = staleSnack.currentVersionNumber;
              const who = staleSnack.currentVersionPublishedBy;
              const when = staleSnack.currentVersionPublishedAt
                ? new Date(staleSnack.currentVersionPublishedAt).toLocaleString()
                : null;
              const parts: string[] = [];
              if (v != null) parts.push(`v${v}`);
              if (who) parts.push(`by ${who}`);
              if (when) parts.push(when);
              const detail = parts.length ? ` (${parts.join(' · ')})` : '';
              return `Another admin published a new version while you were editing${detail}. Reload to base your edits on the latest, or keep editing (the next save will conflict again).`;
            })()}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
